import { useEffect, useState } from "react";
import { createBrowserRouter, Navigate, RouterProvider, useParams } from "react-router-dom";
import ChangeEmail from "./allauth/account/ChangeEmail";
import ChangePassword from "./allauth/account/ChangePassword";
import Login from "./allauth/account/Login";
import Logout from "./allauth/account/Logout";
import Reauthenticate from "./allauth/account/Reauthenticate";
import RequestPasswordReset from "./allauth/account/RequestPasswordReset";
import ResetPassword, { loader as resetPasswordLoader } from "./allauth/account/ResetPassword";
import Signup from "./allauth/account/Signup";
import VerificationEmailSent from "./allauth/account/VerificationEmailSent";
import VerifyEmail, { loader as verifyEmailLoader } from "./allauth/account/VerifyEmail";
import { AnonymousRoute, AuthChangeRedirector, AuthenticatedRoute } from "./allauth/auth";
import ActivateTOTP, { loader as activateTOTPLoader } from "./allauth/mfa/ActivateTOTP";
import AddWebAuthn from "./allauth/mfa/AddWebAuthn";
import AuthenticateRecoveryCodes from "./allauth/mfa/AuthenticateRecoveryCodes";
import AuthenticateTOTP from "./allauth/mfa/AuthenticateTOTP";
import AuthenticateWebAuthn from "./allauth/mfa/AuthenticateWebAuthn";
import DeactivateTOTP from "./allauth/mfa/DeactivateTOTP";
import GenerateRecoveryCodes, { loader as generateRecoveryCodesLoader } from "./allauth/mfa/GenerateRecoveryCodes";
import ListWebAuthn, { loader as listWebAuthnLoader } from "./allauth/mfa/ListWebAuthn";
import MFAOverview, { loader as mfaOverviewLoader } from "./allauth/mfa/MFAOverview";
import ReauthenticateRecoveryCodes from "./allauth/mfa/ReauthenticateRecoveryCodes";
import ReauthenticateTOTP from "./allauth/mfa/ReauthenticateTOTP";
import ReauthenticateWebAuthn from "./allauth/mfa/ReauthenticateWebAuthn";
import RecoveryCodes, { loader as recoveryCodesLoader } from "./allauth/mfa/RecoveryCodes";
import SmsSigninOrUp from "./allauth/SmsSigninOrUp";
import ManageProviders from "./allauth/socialaccount/ManageProviders";
import ProviderCallback from "./allauth/socialaccount/ProviderCallback";
import ProviderSignup from "./allauth/socialaccount/ProviderSignup";
import Sessions from "./allauth/usersessions/Sessions";
import ClaimTeamForm from "./components/ClaimTeamForm";
import ProfileForm from "./components/ProfileForm";
import { FocusedAssignmentProvider } from "./context/FocusedAssignmentContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { RoleProtectedRoute } from "./context/RoleProtectedRoute";
import About from "./screens/About";
import AcceptInvitation from "./screens/AcceptInvitation";
import AccountDelete from "./screens/AccountDelete";
import AccountDownload from "./screens/AccountDownload";
import AssessmentScreen from "./screens/AssessmentScreen";
import BrandingSettings from "./screens/BrandingSettings";
import CoachProfileScreen from "./screens/CoachProfileScreen";
import Contact from "./screens/Contact";
import Dashboard from "./screens/Dashboard";
import DirectoryPage from "./screens/DirectoryPage";
import EntityForm from "./screens/EntityForm";
import EntityList from "./screens/EntityList";
import EntityView from "./screens/EntityView";
import FAQs from "./screens/FAQs";
import RoleFaqsPage from "./screens/faqs/RoleFaqsPage";
import FinalReflection from "./screens/FinalReflection";
import HomePage from "./screens/HomePage";
import HowItWorksPage from "./screens/HowItWorksPage";
import IntakeSurveyPage from "./screens/IntakeSurveyPage";
import LessonPlanView from "./screens/LessonPlanView";
import SessionSchedulePage from "./screens/SessionSchedulePage";
import LiabilityWaiver from "./screens/LiabilityWaiver";
import ManagePaymentAssignment from "./screens/ManagePaymentAssignment";
import MyAccount from "./screens/MyAccount";
import MyProfilePointsScreen from "./screens/MyProfilePointsScreen";
import NotificationsScreen from "./screens/Notifications";
import OnboardingHub from "./screens/OnboardingHub";
import OnboardingScreen from "./screens/OnboardingScreen";
import PaymentAssignmentsPage from "./screens/PaymentAssignments";
import PreAssessmentReview from "./screens/PreAssessmentReview";
import ProfilePage from "./screens/ProfilePage";
import ProgressReport from "./screens/ProgressReport";
import ProgressSurvey from "./screens/ProgressSurvey";
import MeasurementChangeLogPage from "./screens/MeasurementChangeLogPage";
import QualityAssurance from "./screens/QualityAssurance";
import RequestAccessPage from "./screens/RequestAccessPage";
import { PRESET_SESSION_ENGAGEMENT, PRESET_TEAM, serializeVizQuery } from "./screens/reports/catalog";
import ReportsHomePage from "./screens/ReportsHomePage";
import ScreenshotsPage from "./screens/ScreenshotsPage";
import SessionRecordingsPage from "./screens/SessionRecordingsPage";
import SubscriptionsPage from "./screens/SubscriptionsPage";
import TermsOfUse from "./screens/TermsOfUse";
import Trust from "./screens/Trust";
import UserCheckInsScreen from "./screens/UserCheckInsScreen";
import UserView from "./screens/UserView";
import Layout from "./theme/Layout";

function GrowthWeekRedirect() {
  const { week } = useParams<{ week: string }>();
  const n = parseInt(week || '12', 10);
  const safeWeek = Number.isNaN(n) ? 12 : Math.min(12, Math.max(1, n));
  return (
    <Navigate
      to={`/reports?v=${serializeVizQuery(PRESET_TEAM)}&week=${safeWeek}`}
      replace
    />
  );
}

function createRouter() {
  const allRoutes = [
    {
      path: "/",
      element: (
        <AuthChangeRedirector>
          <FocusedAssignmentProvider>
            <OnboardingProvider>
              <Layout />
            </OnboardingProvider>
          </FocusedAssignmentProvider>
        </AuthChangeRedirector>
      ),
      children: [
        {
          path: "/",
          element: <HomePage />
        },
        {
          path: "/join-my-team",
          element: <ClaimTeamForm />
        },
        {
          path: "/invite/:token",
          element: <AcceptInvitation />
        },
        {
          path: "/faqs",
          element: <FAQs />
        },
        { path: "/faqs/athlete", element: <RoleFaqsPage role="athlete" /> },
        { path: "/faqs/guardian", element: <RoleFaqsPage role="guardian" /> },
        { path: "/faqs/coach", element: <RoleFaqsPage role="coach" /> },
        { path: "/faqs/organization", element: <RoleFaqsPage role="organization" /> },
        { path: "/how-it-works", element: <HowItWorksPage /> },
        { path: "/screenshots", element: <ScreenshotsPage /> },
        { path: "/qa", element: <QualityAssurance /> },
        { path: "/qa/changelog", element: <MeasurementChangeLogPage /> },
        {
          path: "/who-we-are",
          element: <About />
        },
        {
          path: "/contact",
          element: <Contact />
        },
        {
          path: "/intake/:lane",
          element: <IntakeSurveyPage />
        },
        {
          path: "/request-access",
          element: (
            <AuthenticatedRoute>
              <RequestAccessPage />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/trust",
          element: <Trust />
        },
        {
          path: "/coaches/:username",
          element: <CoachProfileScreen />
        },
        {
          path: "/terms-of-use",
          element: <TermsOfUse />
        },
        {
          path: "/liability-waiver",
          element: <LiabilityWaiver />
        },
        {
          path: "/account/download",
          element: <AccountDownload />
        },
        {
          path: "/account/delete",
          element: <AccountDelete />
        },
        {
          path: "/account/sms",
          element: (
            <AnonymousRoute>
              <SmsSigninOrUp />
            </AnonymousRoute>
          )
        },
        {
          path: "/account/login",
          element: (
            <AnonymousRoute>
              <Login />
            </AnonymousRoute>
          )
        },
        {
          path: "/account/email",
          element: (
            <AuthenticatedRoute>
              <ChangeEmail />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/account/logout",
          element: <AuthenticatedRoute><Logout /></AuthenticatedRoute>
        },
        {
          path: "/account/oidc/:provider/callback",
          element: <ProviderCallback />
        },
        {
          path: "/account/:provider/callback",
          element: <ProviderCallback />
        },
        {
          path: "/account/provider/signup",
          element: (
            <AnonymousRoute>
              <ProviderSignup />
            </AnonymousRoute>
          )
        },
        {
          path: "/account/providers",
          element: (
            <AuthenticatedRoute>
              <ManageProviders />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/account/signup",
          element: (
            <AnonymousRoute>
              <Signup />
            </AnonymousRoute>
          )
        },
        {
          path: "/account/verify-email",
          element: <VerificationEmailSent />
        },
        {
          path: "/account/verify-email/:key",
          element: <VerifyEmail />,
          loader: verifyEmailLoader
        },
        {
          path: "/account/password/reset",
          element: (
            <AnonymousRoute>
              <RequestPasswordReset />
            </AnonymousRoute>
          )
        },
        {
          path: "/account/password/reset/key/:key",
          element: (
            <AnonymousRoute>
              <ResetPassword />
            </AnonymousRoute>
          ),
          loader: resetPasswordLoader
        },
        {
          path: "/account/password/change",
          element: (
            <AuthenticatedRoute>
              <ChangePassword />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/account/2fa",
          element: (
            <AuthenticatedRoute>
              <MFAOverview />
            </AuthenticatedRoute>
          ),
          loader: mfaOverviewLoader
        },
        {
          path: "/account/reauthenticate",
          element: (
            <AuthenticatedRoute>
              <Reauthenticate />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/account/reauthenticate/totp",
          element: (
            <AuthenticatedRoute>
              <ReauthenticateTOTP />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/account/reauthenticate/recovery-codes",
          element: (
            <AuthenticatedRoute>
              <ReauthenticateRecoveryCodes />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/account/reauthenticate/webauthn",
          element: (
            <AuthenticatedRoute>
              <ReauthenticateWebAuthn />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/account/authenticate/totp",
          element: (
            <AnonymousRoute>
              <AuthenticateTOTP />
            </AnonymousRoute>
          )
        },
        {
          path: "/account/authenticate/recovery-codes",
          element: (
            <AnonymousRoute>
              <AuthenticateRecoveryCodes />
            </AnonymousRoute>
          )
        },
        {
          path: "/account/authenticate/webauthn",
          element: (
            <AnonymousRoute>
              <AuthenticateWebAuthn />
            </AnonymousRoute>
          )
        },
        {
          path: "/account/2fa/totp/activate",
          element: (
            <AuthenticatedRoute>
              <ActivateTOTP />
            </AuthenticatedRoute>
          ),
          loader: activateTOTPLoader
        },
        {
          path: "/account/2fa/totp/deactivate",
          element: (
            <AuthenticatedRoute>
              <DeactivateTOTP />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/account/2fa/recovery-codes",
          element: (
            <AuthenticatedRoute>
              <RecoveryCodes />
            </AuthenticatedRoute>
          ),
          loader: recoveryCodesLoader
        },
        {
          path: "/account/2fa/recovery-codes/generate",
          element: (
            <AuthenticatedRoute>
              <GenerateRecoveryCodes />
            </AuthenticatedRoute>
          ),
          loader: generateRecoveryCodesLoader
        },
        {
          path: "/account/2fa/webauthn",
          element: (
            <AuthenticatedRoute>
              <ListWebAuthn />
            </AuthenticatedRoute>
          ),
          loader: listWebAuthnLoader
        },
        {
          path: "/account/2fa/webauthn/add",
          element: (
            <AuthenticatedRoute>
              <AddWebAuthn />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/account/sessions",
          element: (
            <AuthenticatedRoute>
              <Sessions />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/my-profile",
          element: (
            <AuthenticatedRoute>
              <ProfilePage />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/my-profile/edit",
          element: (
            <AuthenticatedRoute>
              <ProfileForm />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/my-profile/settings",
          element: (
            <AuthenticatedRoute>
              <MyAccount />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/my-profile/points",
          element: (
            <AuthenticatedRoute>
              <MyProfilePointsScreen />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/dashboard",
          element: (
            <AuthenticatedRoute>
              <Dashboard />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/onboarding",
          element: (
            <AuthenticatedRoute>
              <OnboardingHub />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/assignments/:assignment_id/onboarding",
          element: (
            <AuthenticatedRoute>
              <OnboardingScreen />
            </AuthenticatedRoute>
          )
        },
        {
          path: "/directory",
          element: (
            <RoleProtectedRoute requiredRoles={['admin']}>
              <DirectoryPage />
            </RoleProtectedRoute>
          )
        },
        {
          path: "/notifications",
          element: (
            <AuthenticatedRoute>
              <NotificationsScreen />
            </AuthenticatedRoute>
          )
        },
      ]
    }
  ];

  allRoutes[0].children.push({
    path: `/assessments/:id`,
    element: (
      <RoleProtectedRoute requiredRoles={["athlete"]}>
        <AssessmentScreen />
      </RoleProtectedRoute>
    ),
  });

  allRoutes[0].children.push({
    path: `/assignments/:id/final-reflections`,
    element: (
      <RoleProtectedRoute requiredRoles={["athlete"]}>
        <FinalReflection />
      </RoleProtectedRoute>
    ),
  });

  allRoutes[0].children.push({
    path: `/program-progress/:assignment_id/review`,
    element: (
      <AuthenticatedRoute>
        <PreAssessmentReview />
      </AuthenticatedRoute>
    )
  });

  allRoutes[0].children.push({
    path: `/program-progress/:assignment_id`,
    element: (
      <AuthenticatedRoute>
        <ProgressReport />
      </AuthenticatedRoute>
    )
  });

  allRoutes[0].children.push({
    path: `/progress-report/:athlete_id/review`,
    element: (
      <AuthenticatedRoute>
        <PreAssessmentReview />
      </AuthenticatedRoute>
    )
  });

  allRoutes[0].children.push({
    path: `/progress-report/:athlete_id`,
    element: (
      <AuthenticatedRoute>
        <ProgressReport />
      </AuthenticatedRoute>
    )
  });

  allRoutes[0].children.push({
    path: `/account/payments/:assignment_id`,
    element: (
      <AuthenticatedRoute>
        <ManagePaymentAssignment />
      </AuthenticatedRoute>
    )
  });

  allRoutes[0].children.push({
    path: `/account/payments`,
    element: (
      <AuthenticatedRoute>
        <PaymentAssignmentsPage />
      </AuthenticatedRoute>
    )
  });

  allRoutes[0].children.push({
    path: `/account/subscriptions`,
    element: (
      <AuthenticatedRoute>
        <SubscriptionsPage />
      </AuthenticatedRoute>
    )
  });


  // Users routes
  allRoutes[0].children.push({
    path: "/users/:id",
    element: (
      <RoleProtectedRoute requiredRoles={["athlete", "coach", "parent"]}>
        <UserView />
      </RoleProtectedRoute>
    )
  });

  allRoutes[0].children.push({
    path: "/user/:id/checkins",
    element: (
      <RoleProtectedRoute requiredRoles={["athlete", "coach", "parent"]}>
        <UserCheckInsScreen />
      </RoleProtectedRoute>
    )
  });

  // Coach content detail (assignment-scoped links from dashboard / program progress)
  allRoutes[0].children.push({
    path: "/coach-content/:id",
    element: (
      <RoleProtectedRoute requiredRoles={["athlete", "parent", "coach", "admin"]}>
        <EntityView />
      </RoleProtectedRoute>
    )
  });

  // Lesson Plan routes
  allRoutes[0].children.push({
    path: "/lesson-plan/:id",
    element: (
      <RoleProtectedRoute requiredRoles={["athlete", "parent", "coach", "admin"]}>
        <LessonPlanView />
      </RoleProtectedRoute>
    )
  });
  allRoutes[0].children.push({
    path: "/lesson-plan/:id/schedule",
    element: (
      <RoleProtectedRoute requiredRoles={["athlete", "parent", "coach", "admin"]}>
        <SessionSchedulePage />
      </RoleProtectedRoute>
    )
  });
  allRoutes[0].children.push({
    path: "/program-progress/:assignment_id/schedule",
    element: (
      <RoleProtectedRoute requiredRoles={["athlete", "parent", "coach", "admin"]}>
        <SessionSchedulePage />
      </RoleProtectedRoute>
    )
  });
  allRoutes[0].children.push({
    path: "/lesson-plan/:id/week/:weekNum/progress-survey",
    element: (
      <AuthenticatedRoute>
        <ProgressSurvey />
      </AuthenticatedRoute>
    )
  });

  // Session Recordings (filter by lesson_plan_id, session_id, slide_id in query)
  allRoutes[0].children.push({
    path: "/recordings",
    element: (
      <AuthenticatedRoute>
        <SessionRecordingsPage />
      </AuthenticatedRoute>
    )
  });

  // Notifications routes
  allRoutes[0].children.push({
    path: "/notifications",
    element: (

      <RoleProtectedRoute requiredRoles={["athlete", "parent", "coach", "admin"]}>
        <EntityList showFab={true} />
      </RoleProtectedRoute>
    )
  });
  allRoutes[0].children.push({
    path: "/notifications/:id",
    element: (
      <RoleProtectedRoute requiredRoles={["athlete", "parent", "coach", "admin"]}>
        <EntityView />
      </RoleProtectedRoute>
    )
  });

  allRoutes[0].children.push({
    path: `/forms/:model/:id/edit`,
    element: <EntityForm />
  });
  allRoutes[0].children.push({
    path: `/forms/:model/0/add`,
    element: <EntityForm />
  });

  allRoutes[0].children.push({
    path: `/admin/branding`,
    element: (
      <RoleProtectedRoute requiredRoles={['admin', 'agent']} requireAll={false}>
        <BrandingSettings />
      </RoleProtectedRoute>
    )
  });

  allRoutes[0].children.push({
    path: `/reports`,
    element: (
      <RoleProtectedRoute requiredRoles={['admin']}>
        <ReportsHomePage />
      </RoleProtectedRoute>
    )
  });

  allRoutes[0].children.push({
    path: `/reports/team-report`,
    element: <Navigate to={`/reports?v=${serializeVizQuery(PRESET_TEAM)}&week=12`} replace />,
  });

  allRoutes[0].children.push({
    path: `/reports/session-engagement`,
    element: <Navigate to={`/reports?v=${serializeVizQuery(PRESET_SESSION_ENGAGEMENT)}`} replace />,
  });

  allRoutes[0].children.push({
    path: `/reports/growth`,
    element: <Navigate to={`/reports?v=${serializeVizQuery(PRESET_TEAM)}&week=12`} replace />,
  });

  allRoutes[0].children.push({
    path: `/reports/growth/week/:week`,
    element: <GrowthWeekRedirect />,
  });

  allRoutes[0].children.push({
    path: `/reports/surveys`,
    element: <Navigate to="/reports?v=survey-submissions" replace />,
  });

  return createBrowserRouter(allRoutes);
}

export default function Router() {
  // If we create the router globally, the loaders of the routes already trigger
  // even before the <AuthContext/> trigger the initial loading of the auth.
  // state.
  const [router, setRouter] = useState(null);
  useEffect(() => {
    // @ts-ignore
    return setRouter(createRouter());
  }, []);
  return router ? <RouterProvider router={router} /> : null;
}
